'use client';

import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

const MetalBuildingCalculator = dynamic(() => import('./MetalBuildingCalculator'), {
  ssr: false,
  loading: () => <CalculatorPlaceholder />,
});

function CalculatorPlaceholder() {
  return (
    <div
      aria-hidden="true"
      className="mt-10 h-[1400px] animate-pulse rounded-[28px] border border-slate-200 bg-slate-50 md:mt-14 lg:h-[640px]"
    />
  );
}

/**
 * Калькулятор на страницах раздела и товара. Блок ниже первого экрана, а
 * калькулятор тянет three.js, поэтому монтируем его только при подлёте к viewport.
 */
export default function CatalogCalculatorBlock() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  // Ждём load + idle: калькулятор при монтировании подгружает скрипт капчи, и если
  // это случится до конца гидратации стриминговых блоков страницы, React падает
  // с ошибкой гидратации (#418) — так было на товарах, где блок у первого экрана.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let observer: IntersectionObserver | undefined;
    let idleId: number | undefined;

    const observe = () => {
      if (typeof IntersectionObserver === 'undefined') {
        setVisible(true);
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setVisible(true);
            observer?.disconnect();
          }
        },
        { rootMargin: '600px 0px' },
      );
      observer.observe(el);
    };
    const schedule = () => {
      idleId = window.requestIdleCallback
        ? window.requestIdleCallback(observe, { timeout: 2000 })
        : window.setTimeout(observe, 200);
    };

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });

    return () => {
      window.removeEventListener('load', schedule);
      if (idleId !== undefined) {
        if (window.cancelIdleCallback) window.cancelIdleCallback(idleId);
        else window.clearTimeout(idleId);
      }
      observer?.disconnect();
    };
  }, []);

  return (
    <div ref={ref} className="my-16">
      <h2 className="text-3xl font-bold tracking-tight text-blue-900 sm:text-4xl">
        Рассчитайте стоимость здания
      </h2>
      <p className="mt-3 max-w-2xl text-sm text-slate-600 md:text-base">
        Задайте размеры, кровлю и исполнение — посмотрите 3D-модель и получите
        коммерческое предложение с расчётом стоимости.
      </p>
      {visible ? <MetalBuildingCalculator /> : <CalculatorPlaceholder />}
    </div>
  );
}
